/**
 * The booking vocabulary the API and the interface share.
 *
 * Wire values are upper snake case; the French labels live here so a select
 * renders before anything has loaded.
 */
export const PRICE_MODES = [
  { value: 'FIXED', label: 'Prix forfaitaire', needsAmount: true },
  { value: 'HOURLY', label: 'Prix horaire', needsAmount: true },
  { value: 'QUOTE', label: 'Sur devis', needsAmount: false },
];

export const BOOKING_STATUS_COLOR = {
  REQUESTED: 'info',
  CONFIRMED: 'success',
  DECLINED: 'error',
  CANCELLED: 'default',
  COMPLETED: 'primary',
};

export const TASK_RECURRENCES = [
  { value: 'ONCE', label: 'Une seule fois' },
  { value: 'MONTHLY', label: 'Tous les mois' },
  { value: 'QUARTERLY', label: 'Tous les trois mois' },
  { value: 'BIANNUAL', label: 'Deux fois par an' },
  { value: 'YEARLY', label: 'Tous les ans' },
];

export const WEEKDAYS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 7, label: 'Dimanche' },
];

/** "1 h 30" — a duration a person reads, not 90. */
export function formatDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (0 === hours) return `${rest} min`;
  if (0 === rest) return `${hours} h`;

  return `${hours} h ${String(rest).padStart(2, '0')}`;
}

/** "samedi 13 septembre, 09:00", in the reader's own clock. */
export function formatSlot(isoString) {
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "09:00" — just the time, for a grid of slots that already shows the day. */
export function formatTime(isoString) {
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** "samedi 13 septembre" — the heading over a day's slots. */
export function formatDay(isoString) {
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** "14/03/2027", from the API's plain "2027-03-14". */
export function formatDate(value) {
  if (!value) return '';

  const [year, month, day] = String(value).split('-');

  return `${day}/${month}/${year}`;
}

/**
 * Group slots by the local day they fall on.
 *
 * The API returns a flat list in UTC; a person picks a time inside a day, so
 * the grouping has to happen in their clock, not in the server's.
 */
export function groupSlotsByDay(slots) {
  const days = new Map();

  for (const slot of slots) {
    const key = new Date(slot.starts_at).toLocaleDateString('fr-CA');

    if (!days.has(key)) {
      days.set(key, []);
    }

    days.get(key).push(slot);
  }

  return [...days.entries()].map(([key, daySlots]) => ({ key, slots: daySlots }));
}
